import { CurrencyDisplay, GameButton, Modal, PixelIcon } from '@/components/ui';
import { useGame } from '@/contexts/GameContext';
import { useShop } from '@/contexts/ShopContext';
import { WORLDS, getNextUncompletedLevel, getWorldProgress, isWorldUnlocked } from '@/config/worlds';
import { motion } from 'motion/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { puzzles } from '@/game/puzzles';
import type { CSSProperties } from 'react';

interface WorldMapScreenProps {
  onGoHome: () => void;
  onStartLevel: (level: number) => void;
  onOpenShop: () => void;
}

function formatLevelLabel(levelIndex: number) {
  if (levelIndex === 0) return 'Tutorial';
  return `Level ${levelIndex + 1}`;
}

const WorldMapScreen: React.FC<WorldMapScreenProps> = ({ onGoHome, onStartLevel, onOpenShop }) => {
  const { gameState } = useGame();
  const { shopState, shopManager } = useShop();
  const navigate = useNavigate();

  const completed = new Set<number>(gameState.completedLevels || []);
  const totalLevels = puzzles.length;
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };

  return (
    <Modal
      isOpen={true}
      onClose={onGoHome}
      title="World Map"
      subtitle="Choose a world. Chase a perfect route."
      variant="secondary"
      size="fullscreen"
      className="world-map-modal menu-facelift-modal"
      showCloseButton={true}
      headerContent={
        <div className="menu-toolbar">
          <CurrencyDisplay
            coins={shopState.coins}
            gems={shopState.gems}
            lives={shopState.lives}
            streak={streakStatus.streak}
            canClaimStreak={streakStatus.canClaim}
            variant="header"
            showStreak={true}
            animated={true}
          />
          <div className="menu-toolbar__actions">
            <GameButton
              onClick={() => navigate('/levels')}
              variant="secondary"
              size="small"
              icon="levels"
              text="All Levels"
              className="menu-toolbar__button"
            />
            <GameButton
              onClick={onOpenShop}
              variant="warning"
              size="small"
              icon="shop"
              text="Shop"
              className="menu-toolbar__button"
            />
            <GameButton
              onClick={() => navigate('/upgrades')}
              variant="success"
              size="small"
              icon="upgrades"
              text="Upgrades"
              className="menu-toolbar__button"
            />
          </div>
        </div>
      }
    >
      <div className="menu-card-grid world-card-grid">
        {WORLDS.map((world, worldIndex) => {
          const unlocked = isWorldUnlocked(worldIndex, completed, totalLevels);
          const { completedInWorld, totalInWorld, ratio } = getWorldProgress(world, completed, totalLevels);
          const hasLevels = totalInWorld > 0;
          const isComplete = hasLevels && ratio >= 1;

          const nextLevel = hasLevels ? getNextUncompletedLevel(world, completed, totalLevels) : null;
          const canPlay = unlocked && nextLevel !== null;

          const statusLabel = !hasLevels
            ? 'Coming soon'
            : isComplete
              ? 'Complete'
              : unlocked
                ? 'Unlocked'
                : 'Locked';

          return (
            <motion.article
              key={world.id}
              className={`world-card ${world.className} ${!unlocked && hasLevels ? 'is-locked' : ''} ${isComplete ? 'is-complete' : ''}`}
              style={
                {
                  '--world-gradient': world.gradient,
                  '--world-edge': world.visual.edge,
                  '--world-glow': world.visual.glow,
                  '--world-surface': world.visual.surface,
                } as CSSProperties
              }
              whileHover={{ y: -2, scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            >
              <div className="world-card__header">
                <div className="world-card__title-wrap">
                  <div className="world-card__icon">
                    <PixelIcon name={world.icon} size={22} />
                  </div>
                  <div>
                    <p className="world-card__title">{world.name}</p>
                    <p className="world-card__subtitle">{world.subtitle}</p>
                  </div>
                </div>

                <div className="world-card__status-chip">
                  {!hasLevels ? (
                    <PixelIcon name="construction" size={14} color="#f39c12" />
                  ) : !unlocked ? (
                    <PixelIcon name="lock" size={14} color="#bdc3c7" />
                  ) : isComplete ? (
                    <PixelIcon name="star" size={14} color="#ffd600" />
                  ) : (
                    <PixelIcon name="play" size={14} color="#4ecdc4" />
                  )}
                  <span>{statusLabel}</span>
                </div>
              </div>

              <div className="world-card__enemy-panel" aria-label={`${world.enemySetName} enemies`}>
                <p className="world-card__enemy-title">{world.enemySetName}</p>
                <div className="world-card__enemy-strip">
                  {world.enemies.map((enemy) => (
                    <span
                      key={enemy.id}
                      className="world-card__enemy-chip"
                      style={{ '--enemy-accent': enemy.accent } as CSSProperties}
                      title={`${enemy.name}: ${enemy.role}`}
                    >
                      <PixelIcon name={enemy.icon} size={14} color={enemy.accent} />
                      <span>{enemy.name}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="world-card__progress">
                <div className="world-card__progress-meta">
                  <span>{hasLevels ? `${completedInWorld}/${totalInWorld} levels` : '0 levels'}</span>
                  <span>{hasLevels ? `${Math.round(ratio * 100)}%` : '0%'}</span>
                </div>
                <div className="world-card__progress-track">
                  <div
                    className="world-card__progress-fill"
                    style={{ width: `${Math.round(ratio * 100)}%` }}
                  />
                </div>
              </div>

              <div className="world-card__actions">
                <GameButton
                  onClick={() => {
                    if (nextLevel !== null) onStartLevel(nextLevel);
                  }}
                  disabled={!canPlay}
                  variant="primary"
                  size="small"
                  icon="play"
                  text={nextLevel !== null ? `Play ${formatLevelLabel(nextLevel)}` : 'Play'}
                  className="world-card__button"
                />
                <GameButton
                  onClick={() => navigate(`/levels?world=${encodeURIComponent(world.id)}`)}
                  disabled={!unlocked || !hasLevels}
                  variant="secondary"
                  size="small"
                  icon="levels"
                  text="Select Level"
                  className="world-card__button"
                />
              </div>
            </motion.article>
          );
        })}
      </div>
    </Modal>
  );
};

export default WorldMapScreen;
